import { List } from 'immutable';

import * as records from './records';
import { TIME_TYPE } from './constants';

/**
 * Returns the time of when the command ended, based on its start time, duration
 * and time between commands.
 * @param {Object} params
 * @param {number} params.timelineSeconds
 * @param {number} [params.between_commands=0]
 * @param {number} [params.duration=0]
 * @returns {number}
 */
export const getCommandEndTimeSeconds = ({
  timelineSeconds,
  between_commands = 0, // eslint-disable-line camelcase
  duration = 0,
}) => (
  timelineSeconds + between_commands + duration // eslint-disable-line camelcase
);

/**
 * Adds `timelineSeconds` to each command in `logFile`.
 * @param {Object[]} logFile
 * @returns {Object[]} Supplemented commands.
 */
export const generateTimeline = (logFile) => {
  let relativeCommandTime = 0;
  return logFile.map((command) => {
    const commandWithTimeline = ({ ...command, timelineSeconds: relativeCommandTime });
    relativeCommandTime = getCommandEndTimeSeconds(commandWithTimeline);
    return commandWithTimeline;
  });
};

/**
 * Converts an array of Appium commands to a compatible format.
 * @param {Object[]} logFile
 * @returns {Object[]} Converted commands.
 */
export const generateAppiumTimeline = (logFile) => {
  const relativeCommandTime = logFile.length ? logFile[0].timestamp : 0;

  return logFile.map((command) => {
    const {
      httpMethod,
      httpPathInfo,
      requestBody,
      responseBody,
      responseStatus,
      timestamp, // eslint-disable-line @typescript-eslint/no-unused-vars
      ...rest
    } = command;

    let result;

    try {
      result = responseBody?.length ? JSON.parse(responseBody) : null;
    } catch {
      result = responseBody;
    }

    const request = requestBody?.length ? JSON.parse(requestBody) : null;

    return {
      method: httpMethod,
      path: httpPathInfo,
      request,
      result,
      timelineSeconds: (command.timestamp - relativeCommandTime) / 1000,
      HTTPStatus: responseStatus,
      message: httpPathInfo.includes('/sauce-script') && responseStatus === 200 && typeof request?.script === 'string' ?
        request.script.replace('sauce:context=', '') : '',
      ...rest,
    };
  });
};

/**
 * Adds `id` ( = `index`) to each command in `logFile`.
 * We rely on id === index for keyboard navigation.
 * @param {Object[]} logFile
 * @returns {Object[]} Supplemented commands.
 */
export const generateCommandId = (logFile) => (
  logFile.map((command, index) => ({ ...command, id: index }))
);

/**
 * Converts an array of Appium command objects to `RdcJobCommand`s.
 * @param {Object[]} logFile
 * @returns {Object[]} Converted commands.
 */
export const parseAppiumLog = (logFile) => (
  generateAppiumTimeline(generateCommandId(logFile))
    .map((command) => new records.RdcJobCommand(command))
);

/**
 * Converts an array of Selenium/WebDriver command objects to `SeleniumCommand`s.
 * @param {Object[]} logFile
 * @returns {Object[]} Converted commands.
 */
export const parseSeleniumLog = (logFile) => (
  generateTimeline(generateCommandId(logFile))
    .map((command) => new records.SeleniumCommand(command))
);

/**
 * Obtains an element with given `index` from an array or a list.
 * @param {(*[]|Immutable.List<*>)} commands
 * @param {number} index
 * @returns {*} The element under `index`.
 */
function getCommand(commands, index) {
  if (!(commands.size || commands.length)) {
    return null;
  }
  return commands[index] || commands.get(index);
}

/**
 * Finds the first command with `timelineSeconds` exceeding `currentTime`,
 * then returns the previous one (but not if it's the first one).
 * Works with both native JS arrays and `Immutable` lists
 * @param {Object} params
 * @param {(*[]|Immutable.List<*>)} params.commands
 * @param {number} [params.currentTime=0]
 * @param {string} [params.timeType=]
 * @returns {*} First active command.
 */
export const findActiveCommand = ({
  commands,
  currentTime = 0,
  timeType = TIME_TYPE.TIMELINE,
}) => {
  // Guard against last command and no commands:
  const commandsSize = commands.size || commands.length;
  const lastCommand = getCommand(commands, commandsSize - 1);
  if (!lastCommand) {
    return null;
  }
  if (currentTime > lastCommand.getTimeline(timeType)) {
    return lastCommand;
  }

  let index = commands.findIndex((command) => (
    !command.isDummy() && command.getTimeline(timeType) >= currentTime
  ));
  let result;
  do {
    index -= 1;
    result = getCommand(commands, Math.max(0, index));
  } while (result.isDummy() && index > 0);
  return result;
};

/**
 * In order to pretty print titles, we need to map Selenium ID of HTMLNode with
 * CSS selector that matches it. It's available only in 'css selector requests',
 * in other actions only Selenium ID is available.

 * This function maps Selenium IDs to selectors and adds them to every command.
 * @param {(*[]|Immutable.List<*>)} commands
 * @returns {Object}
 */

export const buildSeleniumSelectorsMap = (commands) => (
  commands.reduce((hashMap, command) => {
    let key;
    // Try/catch block guards from unhandled selenium events.
    try {
      if (command.getFromRequest('using') !== 'css selector') {
        return hashMap;
      }
      const result = command.get('result');
      key = Array.isArray(result) ? result[0].ELEMENT : result.ELEMENT;
      if (!key) {
        return hashMap;
      }
    } catch (err) {
      // TODO: add segmentio event to collect information about edge cases and fix them
      return hashMap;
    }

    return {
      ...hashMap,
      [key]: command.get('request').value,
    };
  }, {})
);

/**
 * All commands with `hide_from_ui` === `true` get hidden.

 * Visual commands require processing to handle hidden command durations and failures
 * all Visual hidden command durations will be aggregated and summed with the next visible
 * command if the command is a visual one.

 * Same goes for failed Visual hidden commands: if a hidden command has failed, the next
 * visible one will be marked as a fail by updating the status code.
 * @param {Object[]} payload
 * @returns {Immutable.List<*>}
 */
export const updateVisualCommands = (payload) => {
  let durationTotal = 0;
  let nextVisibleFailed = false;
  return List(payload)
    .map((value) => {
      if (value.visual_command) {
        const isFailed = value.isFailed();
        const isHidden = !!value.hide_from_ui;
        const commandDuration = value.duration;
        let updated = value;
        if (isHidden) {
          durationTotal += commandDuration;
          if (isFailed) {
            nextVisibleFailed = true;
          }
        } else {
          if (!isFailed && nextVisibleFailed) {
            updated = value.update('statusCode', () => 1);
            nextVisibleFailed = false;
          }
          if (durationTotal !== 0) {
            updated = updated.update('duration', () => durationTotal + commandDuration);
            durationTotal = 0;
          }
          return updated;
        }
      }
      return value;
    })
    .filter((value) => !value.hide_from_ui);
};
